import React, { useState, useEffect, useRef, useContext } from 'react';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { Play, Pause, Download, Volume2, Loader2, AlertCircle } from 'lucide-react';
import { getCallArtifactUrl, streamCallArtifact } from '../services/api';

/**
 * ThemedAudioPlayer — fetches call recordings via the backend proxy.
 *
 * Vapi recordings are now in a private bucket — the signed URL must be
 * requested through the backend (which holds the Vapi API key) rather than
 * used directly. This component:
 *   1. On mount, calls GET /api/callsummary/artifact/{callId}/mono-recording/url
 *   2. Receives a short-lived signed URL
 *   3. Sets that URL as the <audio> src
 *
 * Props:
 *   callId       — the Vapi call ID (stored in call_summaries.call_id)
 *   recordingUrl — the old direct URL (used as fallback for existing records)
 *   summaryId    — optional, for display
 *   artifactType — default "mono-recording", can be "stereo-recording" etc.
 */
const ThemedAudioPlayer = ({
  callId,
  recordingUrl,
  summaryId,
  artifactType = 'mono-recording',
}) => {
  const { organizationId } = useContext(OrganizationContext);

  const [selectedType, setSelectedType] = useState(artifactType);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [progress, setProgress]         = useState(0);
  const [duration, setDuration]         = useState(0);
  const [volume, setVolume]             = useState(1);
  const [audioSrc, setAudioSrc]         = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const audioRef = useRef(null);

  // ── Resolve the playable URL on mount ──────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    // Reset player state when type changes
    setIsPlaying(false);
    setProgress(0);
    if (audioRef.current) audioRef.current.pause();

    const resolveAudioUrl = async () => {
      setLoading(true);
      setError(null);
      setAudioSrc(null);

      console.log('ThemedAudioPlayer resolving audio — callId:', callId,
        'type:', selectedType, 'orgId:', organizationId);

      // ── Attempt 1: Backend proxy /url endpoint (signed URL) ──────────────
      // This works for all recordings regardless of when they were stored,
      // as long as the callId is available. Always try this first.
      if (callId && organizationId) {
        try {
          const data = await getCallArtifactUrl(callId, selectedType, organizationId);
          if (!cancelled && data?.url) {
            console.log('Got signed URL via proxy');
            setAudioSrc(data.url);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Signed URL proxy failed:', err.message);
        }

        // ── Attempt 2: Backend proxy streaming endpoint ───────────────────
        // If the /url endpoint fails (e.g. Vapi returns non-302), try the
        // streaming endpoint which returns the audio bytes directly.
        try {
          const blobUrl = await streamCallArtifact(callId, selectedType, organizationId);
          if (!cancelled && blobUrl) {
            console.log('Got blob URL via streaming proxy');
            setAudioSrc(blobUrl);
            setLoading(false);
            return;
          }
        } catch (err) {
          console.warn('Streaming proxy failed:', err.message);
        }
      } else {
        console.warn('No callId or organizationId — skipping proxy attempts',
          { callId, organizationId });
      }

      // ── Attempt 3: Direct recordingUrl (older records only) ──────────────
      // Old direct Vapi URLs are now inaccessible, but kept as last resort
      // in case the org uses custom cloud storage (S3/GCS/R2) which still
      // returns public URLs.
      if (recordingUrl && !cancelled) {
        console.log('Trying direct recordingUrl as last resort');
        setAudioSrc(recordingUrl);
        setLoading(false);
        return;
      }

      if (!cancelled) {
        const msg = callId
          ? 'Recording unavailable. Vapi may not have stored a recording for this call.'
          : 'No call ID found — cannot retrieve recording.';
        setError(msg);
        setLoading(false);
      }
    };

    resolveAudioUrl();
    return () => { cancelled = true; };
  }, [callId, selectedType, organizationId, recordingUrl]);

  // ── Audio element event handlers ───────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return;

    const onTimeUpdate  = () => setProgress((audio.currentTime / audio.duration) * 100 || 0);
    const onLoadedMeta  = () => setDuration(audio.duration);
    const onEnded       = () => setIsPlaying(false);
    const onError       = () => {
      setError('Failed to load recording. The link may have expired — refresh to retry.');
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [audioSrc]);

  // ── Controls ────────────────────────────────────────────────────────────────
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); } else { audio.play(); }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const pct = parseFloat(e.target.value);
    audio.currentTime = (pct / 100) * audio.duration;
    setProgress(pct);
  };

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.volume = v;
    setVolume(v);
  };

  const handleDownload = async () => {
    try {
      // Re-fetch a fresh signed URL for download — the current one may be expiring
      if (callId && organizationId) {
        const data = await getCallArtifactUrl(callId, selectedType, organizationId);
        if (data?.url) {
          const a = document.createElement('a');
          a.href = data.url;
          a.download = `${callId}-${selectedType}.wav`;
          a.click();
          return;
        }
      }
      // Fallback to current audioSrc
      if (audioSrc) {
        const a = document.createElement('a');
        a.href = audioSrc;
        a.download = `${callId || summaryId}-recording.wav`;
        a.click();
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const currentTime = audioRef.current
    ? (progress / 100) * (audioRef.current.duration || 0)
    : 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-3 bg-gray-800 rounded-lg text-gray-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading recording…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-900/30 border border-red-800 rounded-lg text-red-400 text-sm">
        <AlertCircle className="w-4 h-4 shrink-0" />
        {error}
      </div>
    );
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 space-y-2">
      {audioSrc && (
        <audio ref={audioRef} src={audioSrc} preload="metadata" className="hidden" />
      )}

      {/* Controls row */}
      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          className="w-8 h-8 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center shrink-0 transition-colors"
        >
          {isPlaying
            ? <Pause className="w-4 h-4 text-white" />
            : <Play  className="w-4 h-4 text-white ml-0.5" />}
        </button>

        {/* Time */}
        <span className="text-xs text-gray-400 w-10 shrink-0">
          {formatTime(currentTime)}
        </span>

        {/* Progress */}
        <input
          type="range" min="0" max="100" step="0.1"
          value={progress}
          onChange={handleSeek}
          className="flex-1 h-1 accent-blue-500 cursor-pointer"
        />

        <span className="text-xs text-gray-400 w-10 shrink-0 text-right">
          {formatTime(duration)}
        </span>

        {/* Volume */}
        <Volume2 className="w-4 h-4 text-gray-400 shrink-0" />
        <input
          type="range" min="0" max="1" step="0.05"
          value={volume}
          onChange={handleVolume}
          className="w-16 h-1 accent-blue-500 cursor-pointer"
        />

        {/* Download */}
        <button
          onClick={handleDownload}
          title="Download recording"
          className="text-gray-400 hover:text-blue-400 transition-colors"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>

      {/* Artifact type selector */}
      <ArtifactTypeSelector
        currentType={selectedType}
        onSelect={(type) => setSelectedType(type)}
      />
    </div>
  );
};


/**
 * Small selector to switch between mono/stereo/customer/assistant recordings.
 * Calls the parent's onSelect so the parent can re-render with the new type.
 */
const ArtifactTypeSelector = ({ currentType, onSelect }) => {
  const options = [
    { value: 'mono-recording',      label: 'Mono'      },
    { value: 'stereo-recording',    label: 'Stereo'    },
    { value: 'customer-recording',  label: 'Customer'  },
    { value: 'assistant-recording', label: 'Assistant' },
  ];

  return (
    <div className="flex gap-1">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          className={`text-xs px-2 py-0.5 rounded transition-colors ${
            currentType === opt.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
};


export default ThemedAudioPlayer;